import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmDeleteModal from './ConfirmDeleteModal';

/*
 * The one destructive action of a dialog, a drawer or an expanded row, kept
 * away from the way out (2026-10-13). It used to sit in the header, right
 * beside the × close button, where a hand reaching to close could press it.
 * Now it is a section of its own at the foot, ruled off from the record above,
 * its button at the inline start -- the opposite end from the ×, which stays
 * at the top at the inline end -- and it always asks first, through the app's
 * ConfirmDeleteModal. Logical properties only, so right to left mirrors it.
 *
 * Whether to offer it at all is the caller's: render it only when the server
 * said the action is allowed (a record's `actions`).
 *
 * Props:
 *   label, title        the button's text and tooltip
 *   confirmTitle        the confirmation's heading
 *   consequences        what will happen, listed in the confirmation
 *   confirmLabel, confirmingLabel
 *                       the confirmation's own button (default Delete / Deleting...)
 *   failedText          shown when the action fails and the server said nothing
 *   onConfirm()         does it, returning a promise. Rejected, the server's message
 *                       (else failedText) shows in the confirmation, which stays open.
 *   onOpenChange(open)  the confirmation opened or closed (a drawer's Escape defers to it)
 *   ready               false keeps the confirmation's button disabled (a reason not typed yet)
 *   children            in the confirmation, under the consequences: what it needs from the person
 *   testId              the section's data-testid; its button is `${testId}-button`
 */
export default function DangerZone({
  label, title, confirmTitle, consequences, confirmLabel, confirmingLabel, failedText,
  onConfirm, onOpenChange, ready = true, children, testId = 'danger-zone',
}) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const show = (next) => {
    setOpen(next);
    if (next) setError('');
    onOpenChange?.(next);
  };

  const confirm = () => {
    setBusy(true);
    setError('');
    Promise.resolve()
      .then(() => onConfirm?.())
      .then(() => { setBusy(false); show(false); })
      .catch((e) => {
        setBusy(false);
        setError(e?.response?.data?.error || failedText || e?.message || String(e));
      });
  };

  return (
    <section className="danger-zone" aria-label={t('dangerZone')} data-testid={testId} style={zoneStyle}>
      <button type="button" onClick={() => show(true)} title={title} data-testid={`${testId}-button`}
        style={buttonStyle}>
        {label}
      </button>
      <ConfirmDeleteModal
        open={open}
        title={confirmTitle}
        consequences={consequences}
        deleting={busy}
        error={error}
        confirmLabel={confirmLabel}
        confirmingLabel={confirmingLabel}
        confirmDisabled={!ready}
        onConfirm={confirm}
        onCancel={() => { if (!busy) show(false); }}>
        {children}
      </ConfirmDeleteModal>
    </section>
  );
}

const zoneStyle = {
  marginBlockStart: 24,
  paddingBlockStart: 16,
  borderBlockStart: '1px solid var(--c-border)',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  gap: 10,
};

const buttonStyle = {
  background: 'none',
  color: 'var(--c-danger-fg)',
  border: '1px solid var(--c-danger)',
  borderRadius: 6,
  padding: '6px 14px',
  fontSize: 13,
  cursor: 'pointer',
};
